import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GlobalStore } from '@highlight/ipc'
import { useGlobalStore } from '@highlight/ipc/react'
import { useAuth } from '@renderer/chat/providers'
import { useUser } from '@renderer/hooks/useUser'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import type { LegalDocumentType } from '@highlight/common'

export interface LegalObligationVersion {
  id: string
  versionNumber: number
  status: 'scheduled' | 'live' | 'archived'
  changeType: 'minor' | 'material'
  changeSummary: string
  publishedAt: string
  effectiveAt: string
}

/** One document as the signed-in user owes it. */
export interface LegalObligation {
  type: LegalDocumentType
  title: string
  current: LegalObligationVersion | null
  accepted: boolean
  /** Whether any version of the document was ever accepted. */
  everAccepted: boolean
  /** An unaccepted material version: the app blocks until it is accepted. */
  pending: boolean
  /** Legal's summary of the material change owed; null when nothing is pending. */
  pendingSummary: string | null
}

export type LegalObligationWithVersion = LegalObligation & { current: LegalObligationVersion }

/** The documents with a published version: the only ones there is anything to read or accept. */
export function withCurrentVersion(documents: Array<LegalObligation> | undefined): Array<LegalObligationWithVersion> {
  return (documents ?? []).filter((document): document is LegalObligationWithVersion => document.current !== null)
}

export interface LegalObligations {
  documents: Array<LegalObligation>
  pending: boolean
}

export interface LegalAcceptance {
  versionIds: Array<string>
  source: 'signup' | 'in_app_prompt'
}

/** The Legal CMS rollout switch; off, the acceptance prompt does not exist. */
export const LEGAL_CMS_FLAG = 'legal-cms'

/** Keyed by user, so a sign-out followed by another sign-in never reads the previous account's answer. */
export const legalObligationsQueryKey = (userId: string | undefined) => ['legal', 'obligations', userId ?? null]

/** An acceptance the user gave while offline, replayed on the next open for that same user; the server ignores repeats. */
export const LEGAL_QUEUED_ACCEPTANCE_STORE_KEY = 'legal.queuedAcceptance'
/** Bumped on every recorded acceptance: each window has its own query cache, and this tells the others to ask again. */
export const LEGAL_ACCEPTED_AT_STORE_KEY = 'legal.acceptedAt'

interface QueuedLegalAcceptance {
  userId: string
  acceptance: LegalAcceptance
}

async function unwrap<T>(response: Response, failure: string): Promise<T> {
  if (!response.ok) {
    throw new Error(`${failure} (${response.status})`)
  }
  const json = (await response.json()) as { data: T }
  return json.data
}

export function useLegalObligations(enabled: boolean) {
  const { getAuthToken } = useAuth()
  const userId = useUser()?.id
  const queryClient = useQueryClient()
  const [acceptedAt] = useGlobalStore<number | null>('general', LEGAL_ACCEPTED_AT_STORE_KEY, null)
  useEffect(() => {
    if (acceptedAt !== null && userId) {
      void queryClient.invalidateQueries({ queryKey: legalObligationsQueryKey(userId) })
    }
  }, [acceptedAt, userId, queryClient])
  return useQuery({
    queryKey: legalObligationsQueryKey(userId),
    enabled: enabled && userId !== undefined,
    // A window that regains focus always asks again, so an acceptance given on another device clears the prompt here.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 15 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const response = await fetch(getChatApiRoute('api/v1/legal/obligations'), {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      })
      return unwrap<LegalObligations>(response, 'Failed to load legal obligations')
    },
  })
}

/** A fetch that never reached the server; anything else is a real answer and is not retried blindly. */
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError
}

/**
 * Records acceptances. With `queueOffline`, a network failure parks them for
 * `useFlushQueuedLegalAcceptance`; the flush itself runs without it, so a failed replay waits for
 * the next signed-in open instead of re-queueing and firing again.
 */
export function useAcceptLegalVersions({ queueOffline = true } = {}) {
  const { getAuthToken } = useAuth()
  const userId = useUser()?.id
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (acceptance: LegalAcceptance) => {
      const response = await fetch(getChatApiRoute('api/v1/legal/acceptances'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getAuthToken()}` },
        body: JSON.stringify(acceptance),
      })
      return unwrap<LegalObligations>(response, 'Failed to record acceptance')
    },
    onSuccess: async (obligations) => {
      // A fetch that left before the acceptance committed would land after it and re-open the prompt.
      await queryClient.cancelQueries({ queryKey: legalObligationsQueryKey(userId) })
      queryClient.setQueryData(legalObligationsQueryKey(userId), obligations)
      GlobalStore.general().set(LEGAL_ACCEPTED_AT_STORE_KEY, Date.now())
    },
    onError: (error, acceptance) => {
      if (queueOffline && userId && isNetworkError(error)) {
        const queued: QueuedLegalAcceptance = { userId, acceptance }
        GlobalStore.general().set(LEGAL_QUEUED_ACCEPTANCE_STORE_KEY, queued)
      }
    },
  })
}

/**
 * Replays an acceptance queued offline by the signed-in user, once per signed-in open. A queue
 * left by another account is dropped rather than written onto this one.
 */
export function useFlushQueuedLegalAcceptance(enabled: boolean) {
  const [queued] = useGlobalStore<QueuedLegalAcceptance | null>('general', LEGAL_QUEUED_ACCEPTANCE_STORE_KEY, null)
  const userId = useUser()?.id
  const { mutate } = useAcceptLegalVersions({ queueOffline: false })
  const attemptedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!enabled || !queued || !userId) return
    if (queued.userId !== userId) {
      GlobalStore.general().delete(LEGAL_QUEUED_ACCEPTANCE_STORE_KEY)
      return
    }
    if (attemptedFor.current === userId) return
    attemptedFor.current = userId
    mutate(queued.acceptance, {
      onSuccess: () => GlobalStore.general().delete(LEGAL_QUEUED_ACCEPTANCE_STORE_KEY),
      onError: (error) => {
        if (!isNetworkError(error)) GlobalStore.general().delete(LEGAL_QUEUED_ACCEPTANCE_STORE_KEY)
      },
    })
  }, [enabled, queued, userId, mutate])
}
